/**
 * EmptyState — placeholder shown when a collection has no visible entries.
 *
 * Single responsibility: communicate "nothing here" with optional title,
 * description, icon, and action slot.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY } from "../design-system/tokens.js";

export interface EmptyStateProps {
  /** Short title describing the state. */
  readonly title: string;
  /** Optional supporting description. */
  readonly description?: string;
  /** Optional emoji or small element rendered above the title. */
  readonly icon?: React.ReactNode;
  /** Optional action element (e.g. a "Clear filters" button). */
  readonly action?: React.ReactNode;
}

/** Renders a centered empty-state placeholder. */
export const EmptyState = React.memo(function EmptyState({
  title,
  description,
  icon,
  action,
}: EmptyStateProps): React.JSX.Element {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "2rem 1rem",
        color: GRAY[600],
        backgroundColor: GRAY[100],
        border: `1px dashed ${GRAY[300]}`,
        borderRadius: "0.5rem",
      }}
    >
      {icon !== undefined && (
        <div style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }} aria-hidden="true">
          {icon}
        </div>
      )}
      <div
        style={{
          fontFamily: FONT.heading,
          fontSize: "0.9375rem",
          fontWeight: 600,
          color: GRAY[700],
        }}
      >
        {title}
      </div>
      {description !== undefined && (
        <p style={{ margin: "0.25rem 0 0", fontSize: "0.8125rem", color: GRAY[600] }}>
          {description}
        </p>
      )}
      {action !== undefined && <div style={{ marginTop: "0.75rem" }}>{action}</div>}
    </div>
  );
});
